import React, { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { StickyHeaderNavigation, CurrencyAvatar } from '@/components'
import { ChevronDown } from '@/assets/icons'
import {
  WithdrawAmountDisplay,
  WithdrawConversionRate,
  WithdrawNumberPad,
  WithdrawButtonConfirmation,
  ResetWithdrawOnRouteEntry,
  WithdrawCurrencyBottomSheet,
  WithdrawMaximumButton,
} from '@/features/withdraw'
import { SelectBlockchainModal } from '@/features/currency-deposit/components'
import { useGetBlockchainsCatalogs } from '@/domain/blockchains/useCases/useGetBlockchainsCatalogs'
import type { BlockchainCatalogsDTO } from '@/domain/blockchains/blockchainsApi'
import { useWithdrawStore } from '@/store'
import {
  useGetFeaturesSettings,
  useGetWallet,
  type WalletCurrency,
} from '@/domain'
import { useUserStore } from '@/store/useUserStore'
import { useTheme } from '@/hooks/useTheme'
import useOffsets from '@/hooks/useOffsets'
import { useAppAnalytics } from '@/services/analytics'

export const WithdrawScreen: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const offsets = useOffsets()
  const { trackEvent, events } = useAppAnalytics()
  const theme = useTheme()
  const hasIdentityVerified = useUserStore(
    (state) => state.userAuthenticated?.hasIdentityVerified,
  )

  const { setSelectedCryptoWalletData, selectedCryptoWalletData } =
    useWithdrawStore()

  // Store state for currencies modal
  const isCurrencyModalOpen = useWithdrawStore(
    (state) => state.isCurrencyModalOpen,
  )
  const setIsCurrencyModalOpen = useWithdrawStore(
    (state) => state.setIsCurrencyModalOpen,
  )

  // Store state for blockchains modal
  const isBlockchainModalOpen = useWithdrawStore(
    (state) => state.isBlockchainModalOpen,
  )
  const setIsBlockchainModalOpen = useWithdrawStore(
    (state) => state.setIsBlockchainModalOpen,
  )
  const setBlockchain = useWithdrawStore((state) => state.setBlockchain)
  const selectedBlockchain = useWithdrawStore((state) => state.blockchain)

  // Get wallet data and feature settings
  const { data: walletData } = useGetWallet()
  const { data: featureSettings } = useGetFeaturesSettings()
  const completeCurrencies = useUserStore((s) => s.wallet?.completeCurrencies)

  // Get currency data from location.state (passed from CryptoActions)
  const currencyFromState =
    location.state?.currency || location.state?.selectedCurrency

  // Extract cryptoCode for passing to ResetWithdrawOnRouteEntry
  const cryptoCodeFromState =
    currencyFromState?.code || location.state?.cryptoCode

  // Get source page for back navigation
  // Save to store to persist between navigations
  const fromPageInState = location.state?.from
  const [fromPage, setFromPage] = React.useState<string | undefined>(fromPageInState)

  // Update fromPage when location.state changes
  React.useEffect(() => {
    if (fromPageInState) {
      setFromPage(fromPageInState)
    }
  }, [fromPageInState])

  // Get blockchain list for selected currency
  const { data: blockchains } = useGetBlockchainsCatalogs({
    currency_isin: selectedCryptoWalletData?.code || 'USDT',
  })

  // Safe access to blockchains array
  const blockchainsList =
    (blockchains as { blockchains: BlockchainCatalogsDTO[] } | undefined)
      ?.blockchains || []

  // Filter currencies by available on_ramp (as in RN version)
  const filteredCurrencies = useMemo(() => {
    // Get current currencies from wallet
    const allCurrencies =
      walletData?.completeCurrencies || completeCurrencies || []

    // Filter currencies by feature settings (as in RN version)
    const availableSelectorCurrencies =
      featureSettings?.features.withdraw.currencies?.items
    if (!availableSelectorCurrencies?.length) {
      return allCurrencies
    }

    return allCurrencies.filter((currency) =>
      availableSelectorCurrencies.some(
        (availableCurrency: any) => availableCurrency === currency.code,
      ),
    )
  }, [
    walletData?.completeCurrencies,
    completeCurrencies,
    featureSettings?.features.withdraw.currencies?.items,
  ])

  // Set currency from state on load
  useEffect(() => {
    if (!hasIdentityVerified) {
      navigate('/validations-by-feature?feature=withdraw')
      return
    }
    if (currencyFromState && !selectedCryptoWalletData) {
      setSelectedCryptoWalletData(currencyFromState)
    } else if (!selectedCryptoWalletData && filteredCurrencies.length > 0) {
      // Select USDT by default or first available currency
      const defaultCurrency =
        filteredCurrencies.find((currency) => currency.code === 'USDT') ||
        filteredCurrencies[0]
      setSelectedCryptoWalletData(defaultCurrency)
    }
  }, [
    currencyFromState,
    selectedCryptoWalletData,
    filteredCurrencies,
    setSelectedCryptoWalletData,
    hasIdentityVerified,
    navigate,
    trackEvent,
    events,
  ])

  // Track Start Event once default currency is set
  useEffect(() => {
    if (selectedCryptoWalletData) {
      trackEvent(events.CRYPTO_ACTION_STARTED, {
        action_name: 'withdraw',
        token_symbol: selectedCryptoWalletData.code,
      })
    }
  }, [selectedCryptoWalletData, trackEvent, events])

  const handleCurrencySelect = () => {
    setIsCurrencyModalOpen(true)
  }

  const handleCloseCurrencyModal = () => {
    setIsCurrencyModalOpen(false)
  }

  const handleCurrencyChange = (currency: WalletCurrency) => {
    setSelectedCryptoWalletData(currency)
    handleCloseCurrencyModal()
  }

  const handleCloseBlockchainModal = () => {
    setIsBlockchainModalOpen(false)
  }

  const handleBlockchainSelect = (blockchain: BlockchainCatalogsDTO) => {
    setBlockchain(blockchain)
    handleCloseBlockchainModal()
    // After blockchain selection, navigate to confirmation
    setTimeout(() => navigate('/withdraw/review-and-confirm', {
      state: { from: fromPage },
    }), 200)
  }

  const handleBackPress = () => {
    // If there is a saved source page, navigate back there
    // Otherwise navigate back in history
    if (fromPage) {
      // For /currency-details, pass full currency object from completeCurrencies
      if (fromPage === '/currency-details' && cryptoCodeFromState) {
        const fullCurrency = completeCurrencies?.find(
          (currency) => currency.code === cryptoCodeFromState,
        )
        navigate(fromPage, {
          state: fullCurrency || currencyFromState || undefined,
        })
      } else {
        // For other pages, pass what was in state
        navigate(fromPage, {
          state: currencyFromState ? { currency: currencyFromState } : undefined,
        })
      }
    } else {
      navigate(-1)
    }
  }

  // Check if there are currencies in the wallet
  if (!filteredCurrencies?.length) {
    return (
      <BaseView flex={1} center>
        <BaseText>{'No currencies available'}</BaseText>
      </BaseView>
    )
  }

  return (
    <>
      <ResetWithdrawOnRouteEntry cryptoCode={cryptoCodeFromState} />

      <BaseView
        flex={1}
        bgColor="background.screen"
        style={{
          height: '100vh',
          paddingTop: offsets.top + 56,
        }}
      >
        {/* Sticky Header */}
        <StickyHeaderNavigation
          title={t('form:actions.withdraw')}
          onBackPress={handleBackPress}
        />

        <BaseView
          flex={1}
          px={12}
          // pt={70}
          // pb={16}
          className="flex flex-col h-full"
          // style={{ minHeight: 'calc(100vh - 70px)' }}
        >
          {/* Central area with amount display */}
          <BaseView
            align="center"
            gap={14}
            className="flex-1 flex flex-col justify-start"
            style={
              {
                // paddingTop: 28,
                // paddingBottom: 20,
              }
            }
          >
            <BaseView gap={12} center>
              <WithdrawAmountDisplay />
              <WithdrawConversionRate />
              <WithdrawMaximumButton />
            </BaseView>

            {/* Currency Selector */}
            <BaseView center mb={20}>
              <BaseView
                onClick={handleCurrencySelect}
                className="px-4 py-3 rounded-full cursor-pointer hover:opacity-80 transition-opacity duration-200"
                style={{
                  backgroundColor: theme.colors.background.cardItemRoot,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {/* Currency icon - as in RN CurrencySelector */}
                <CurrencyAvatar
                  uri={selectedCryptoWalletData?.icon}
                  bgColor={selectedCryptoWalletData?.avatarBGRefColor}
                  size={24}
                  iconSize={15}
                  fallbackText={
                    selectedCryptoWalletData?.code?.substring(0, 2) || '?'
                  }
                />

                {/* Currency code */}
                <BaseText
                  fontSize={16}
                  fontFamily="semibold"
                  color="label.default"
                >
                  {selectedCryptoWalletData?.code}
                </BaseText>

                {/* Down arrow */}
                <ChevronDown size={16} color={theme.colors.label.default} />
              </BaseView>
            </BaseView>
          </BaseView>

          {/* Bottom area with keyboard and button - fixed at bottom */}
          <BaseView
            className="flex-shrink-0"
            style={{
              gap: 4,
            }}
          >
            <WithdrawNumberPad />
            <div style={{ paddingBottom: offsets.bottom }}>
              <WithdrawButtonConfirmation />
            </div>
          </BaseView>
        </BaseView>

        {/* Currency Bottom Sheet */}
        <WithdrawCurrencyBottomSheet
          isOpen={isCurrencyModalOpen}
          onClose={handleCloseCurrencyModal}
          currencies={filteredCurrencies}
          selectedCurrency={selectedCryptoWalletData}
          onSelectCurrency={handleCurrencyChange}
        />

        {/* Blockchain Selection Modal */}
        <SelectBlockchainModal
          isOpen={isBlockchainModalOpen}
          onClose={handleCloseBlockchainModal}
          onSelect={handleBlockchainSelect}
          blockchains={blockchainsList}
          selectedBlockchain={selectedBlockchain}
          currencyCode={selectedCryptoWalletData?.code || ''}
        />
      </BaseView>
    </>
  )
}
